import { createContext, useCallback, useContext, useMemo, useState } from "react";

type ImpersonationState = {
  activeCustomerId: string | null;
  activeCustomerEmail: string | null;
  activeCustomerName: string | null;
};

type ImpersonationContextValue = ImpersonationState & {
  isImpersonating: boolean;
  setImpersonation: (payload: Partial<ImpersonationState> & { activeCustomerId: string }) => void;
  clearImpersonation: () => void;
};

const STORAGE_KEY = "ss_impersonation_context";

const defaultState: ImpersonationState = {
  activeCustomerId: null,
  activeCustomerEmail: null,
  activeCustomerName: null,
};

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

const readStoredState = (): ImpersonationState => {
  if (typeof window === "undefined") return defaultState;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw) as Partial<ImpersonationState>;
    return {
      ...defaultState,
      activeCustomerId: parsed.activeCustomerId ?? null,
      activeCustomerEmail: parsed.activeCustomerEmail ?? null,
      activeCustomerName: parsed.activeCustomerName ?? null,
    };
  } catch {
    return defaultState;
  }
};

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ImpersonationState>(() => readStoredState());

  const setImpersonation = useCallback((payload: Partial<ImpersonationState> & { activeCustomerId: string }) => {
    setState((prev) => {
      const next = {
        ...prev,
        ...payload,
      };
      if (typeof window !== "undefined") {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            activeCustomerId: next.activeCustomerId,
            activeCustomerEmail: next.activeCustomerEmail,
            activeCustomerName: next.activeCustomerName,
          }),
        );
      }
      return next;
    });
  }, []);

  const clearImpersonation = useCallback(() => {
    setState(defaultState);
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      isImpersonating: Boolean(state.activeCustomerId),
      setImpersonation,
      clearImpersonation,
    }),
    [state, setImpersonation, clearImpersonation],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export const useImpersonation = () => {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error("useImpersonation must be used within ImpersonationProvider");
  }
  return ctx;
};
